import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { fetchGbpStatus, fetchInsights, fetchMorningSummary, fetchPendingDispositions, fetchWhatsAppTeamInsights, regenerateMorningSummary } from '../api';
import type { Insights, MorningSummary } from '../types';
import { STATUS_COLOR, STATUS_LABEL, STATUSES } from '../types';
import AdminDashboardSnapshots from './AdminDashboardSnapshots';
import AdminAnalyticsDashboard from './AdminAnalyticsDashboard';
import InsightsWorkflowPrototype from './InsightsWorkflowPrototype';
import { canCopyMorningSummary } from '../utils/morning-summary';
import { useWhatsAppListWebSocket } from '../hooks/useWhatsAppListWebSocket';

function Card({
  label,
  value,
  sub,
  info,
}: {
  label: string;
  value: string | number;
  sub?: string;
  info?: {
    title: string;
    description: string;
    example: string;
  };
}) {
  const infoButtonRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});

  useEffect(() => {
    if (!showInfo || !infoButtonRef.current) return;

    const updatePosition = () => {
      const buttonRect = infoButtonRef.current?.getBoundingClientRect();
      const popupRect = popupRef.current?.getBoundingClientRect();
      if (!buttonRect || !popupRect) return;

      const gap = 10;
      const viewportPadding = 8;
      const popupWidth = Math.min(320, window.innerWidth - viewportPadding * 2);
      const popupHeight = popupRect.height || 180;

      let left = buttonRect.right + gap;
      let top = buttonRect.top - 8;

      if (left + popupWidth > window.innerWidth - viewportPadding) {
        left = buttonRect.left - gap - popupWidth;
      }
      if (left < viewportPadding) {
        left = viewportPadding;
      }

      if (top + popupHeight > window.innerHeight - viewportPadding) {
        top = Math.max(viewportPadding, buttonRect.bottom - popupHeight);
      }
      if (top < viewportPadding) {
        top = viewportPadding;
      }

      setPopupStyle({
        position: 'fixed',
        left,
        top,
        width: popupWidth,
        zIndex: 60,
      });
    };

    updatePosition();

    const onDismiss = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (popupRef.current?.contains(target as Node) || infoButtonRef.current?.contains(target as Node)) {
        return;
      }
      setShowInfo(false);
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('mousedown', onDismiss);
    window.addEventListener('touchstart', onDismiss);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('mousedown', onDismiss);
      window.removeEventListener('touchstart', onDismiss);
    };
  }, [showInfo]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        {info && (
          <div className="relative flex-none">
            <button
              type="button"
              ref={infoButtonRef}
              onClick={() => setShowInfo((value) => !value)}
              className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-500"
              aria-label={`${label} info`}
              aria-expanded={showInfo}
            >
              i
            </button>
            {showInfo && (
              <div
                ref={popupRef}
                style={popupStyle}
                className="rounded-xl border border-slate-200 bg-slate-900 px-3 py-2 text-left text-xs text-white shadow-2xl"
              >
                <div className="font-semibold text-white">{info.title}</div>
                <div className="mt-1 leading-5 text-slate-200">{info.description}</div>
                <div className="mt-2 rounded-lg bg-slate-800 px-2 py-1 leading-5 text-slate-100">
                  Example: {info.example}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function Bar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="mb-2">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-slate-700">{label}</span>
        <span className="text-slate-500">
          {count} <span className="text-slate-400">({pct.toFixed(0)}%)</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function formatPresenceLabel(status: string, stale: boolean) {
  if (stale) return 'Stale';
  if (status === 'on_call') return 'On call';
  if (status === 'break') return 'Break';
  if (status === 'busy') return 'Busy';
  if (status === 'available') return 'Available';
  return 'Offline';
}

function presencePillClass(status: string, stale: boolean) {
  if (stale) return 'bg-slate-100 text-slate-700 ring-slate-200';
  if (status === 'available') return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  if (status === 'busy' || status === 'on_call') return 'bg-amber-50 text-amber-700 ring-amber-200';
  if (status === 'break') return 'bg-orange-50 text-orange-700 ring-orange-200';
  return 'bg-slate-50 text-slate-600 ring-slate-200';
}

function formatLastSeen(minutes: number | null) {
  if (minutes === null) return 'No signal yet';
  if (minutes <= 0) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
}

export default function InsightsTab({
  active,
  reloadKey,
  role,
  onOpenLead,
}: {
  active: boolean;
  reloadKey: number;
  role: string;
  onOpenLead: (id: string) => void;
}) {
  const canViewTeam = role === 'admin' || role === 'manager' || role === 'super_admin';
  const canViewMorningBrief = role === 'admin' || role === 'super_admin';
  const canViewAdminReports = role === 'admin' || role === 'super_admin';
  const defaultScope: 'team' | 'me' = role === 'manager' ? 'me' : canViewTeam ? 'team' : 'me';
  const [scope, setScope] = useState<'team' | 'me'>(defaultScope);
  const effectiveScope = canViewTeam ? scope : 'me';
  const [data, setData] = useState<Insights | null>(null);
  const [summary, setSummary] = useState<MorningSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [summaryErr, setSummaryErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [showAttendance, setShowAttendance] = useState(false);
  const [showDispositions, setShowDispositions] = useState(false);
  const [showAdminReports, setShowAdminReports] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingDispositionCount, setPendingDispositionCount] = useState(0);
  const [gbpStatus, setGbpStatus] = useState<Awaited<ReturnType<typeof fetchGbpStatus>> | null>(null);
  const [whatsappTeamInsights, setWhatsappTeamInsights] = useState<{ unique_replied_today: number; unread_whatsapp: number } | null>(null);
  const loadedKeyRef = useRef<string | null>(null);
  const summaryRequestRef = useRef(0);
  const [summaryClock, setSummaryClock] = useState(Date.now());
  const summaryCopyable = canCopyMorningSummary(summary, effectiveScope, summaryClock);
  const loadKey = useMemo(() => `${reloadKey}:${effectiveScope}`, [effectiveScope, reloadKey]);

  const loadInsights = useCallback(
    async (force = false) => {
      if (!active) return;
      if (!force && loadedKeyRef.current === loadKey) return;
      setRefreshing(true);
      setLoading(true);
      const requestId = ++summaryRequestRef.current;
      loadedKeyRef.current = null;
      setSummary(null);
      setSummaryErr(null);

      const morningPromise = canViewMorningBrief
        ? (force
            ? regenerateMorningSummary(effectiveScope)
            : fetchMorningSummary(effectiveScope)
          ).catch((e) => {
            if (requestId === summaryRequestRef.current) setSummaryErr(e instanceof Error ? e.message : 'Morning summary unavailable');
            return null;
          })
        : Promise.resolve(null);

      void morningPromise.then((morning) => {
        if (morning && requestId === summaryRequestRef.current) {
          setSummary(morning);
          setSummaryClock(Date.now());
          setSummaryErr(null);
        }
      });

      try {
        const [d, dispositionResult, gbpResult, whatsappResult] = await Promise.all([
          fetchInsights(effectiveScope),
          fetchPendingDispositions({ mine: !canViewTeam || effectiveScope === 'me' }).catch(() => ({ items: [] })),
          canViewTeam ? fetchGbpStatus().catch(() => null) : Promise.resolve(null),
          canViewTeam && effectiveScope === 'team' ? fetchWhatsAppTeamInsights().catch(() => null) : Promise.resolve(null),
        ]);
        if (requestId !== summaryRequestRef.current) return;
        setData(d);
        setPendingDispositionCount(dispositionResult.items.length);
        setGbpStatus(gbpResult);
        setWhatsappTeamInsights(whatsappResult);
        setErr(null);
        loadedKeyRef.current = loadKey;
      } catch (e) {
        if (requestId === summaryRequestRef.current) setErr(e instanceof Error ? e.message : 'load failed');
      } finally {
        await morningPromise;
        if (requestId === summaryRequestRef.current) {
          setRefreshing(false);
          setLoading(false);
        }
      }
    },
    [active, canViewTeam, canViewMorningBrief, effectiveScope, loadKey]
  );

  useWhatsAppListWebSocket(
    active && canViewTeam && effectiveScope === 'team',
    'team',
    () => {},
    undefined,
    () => {
      void fetchWhatsAppTeamInsights().then(setWhatsappTeamInsights).catch(() => {});
    },
  );

  useEffect(() => {
    if (!active || !canViewMorningBrief) return;
    const tick = () => setSummaryClock(Date.now());
    const timer = window.setInterval(tick, 30_000);
    window.addEventListener('focus', tick);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', tick); };
  }, [active, canViewMorningBrief]);

  useEffect(() => {
    if (!active) return;
    if (loadedKeyRef.current === loadKey) return;
    void loadInsights();
  }, [active, loadInsights, loadKey, reloadKey, scope]);

  const fallback: Insights = {
    scope: 'team',
    totals: { total_leads: 0, total_calls: 0, total_seconds: 0, unassigned: 0 },
    today: { new_leads: 0, new_lead_calls: 0, calls_made: 0, call_attempts: 0, total_attempts: 0, total_calls: 0, connected_calls: 0, wins_today: 0, registrations_today: null, whatsapp_leads: 0 },
    by_status: {},
    by_tier: {},
    by_source: {},
    by_campaign: {},
    source_funnels: {
      whatsapp: { total: 0, reached: 0, qualified: 0, positive: 0 },
      social: { total: 0, reached: 0, qualified: 0, positive: 0 },
      call_signals: { total_signals: 0, contacted: 0, qualified: 0, positive: 0 },
    },
    conversion_rate: 0,
    top_telecallers: [],
      manager_report: {
        agent_performance: [],
        attendance_summary: { total: 0, available: 0, busy: 0, on_call: 0, break: 0, offline: 0, stale: 0 },
        followups: { pending_due: 0, overdue: 0, due_today: 0, pending_total: 0 },
        disposition_reasons: [],
        dispositions: {
          total_disposed_count: 0,
          disposed_connected_count: 0,
          disposed_not_connected_count: 0,
          whatsapp_contact_count: 0,
          converted_today: 0,
        },
      },
    };
  const { totals, today, by_status, by_tier, by_source, by_campaign, source_funnels, conversion_rate, top_telecallers, manager_report } =
    data ?? fallback;
  const defaultManagerReport = {
    agent_performance: [],
    attendance_summary: { total: 0, available: 0, busy: 0, on_call: 0, break: 0, offline: 0, stale: 0 },
    followups: { pending_due: 0, overdue: 0, due_today: 0, pending_total: 0 },
    disposition_reasons: [],
    dispositions: {
      total_disposed_count: 0,
      disposed_connected_count: 0,
      disposed_not_connected_count: 0,
      whatsapp_contact_count: 0,
      converted_today: 0,
      registered_today: null,
    },
  };
  const sourceFunnels = source_funnels ?? fallback.source_funnels;
  const managerReport = manager_report ?? defaultManagerReport;
  const agentPerformance = managerReport?.agent_performance ?? [];
  const attendanceSummary = managerReport?.attendance_summary ?? defaultManagerReport.attendance_summary;
  const followupSummary = managerReport?.followups ?? defaultManagerReport.followups;
  const dispositionReasons = managerReport?.disposition_reasons ?? [];
  const dispositionSummary = managerReport?.dispositions ?? defaultManagerReport.dispositions;
  const totalLeads = totals.total_leads || 1;
  const totalSources = Object.values(by_source).reduce((a, b) => a + b, 0) || 1;
  const totalCampaigns = Object.values(by_campaign).reduce((a, b) => a + b, 0) || 1;
  const avgCall = totals.total_calls > 0 ? Math.round(totals.total_seconds / totals.total_calls) : 0;
  const copyMorningSummary = async () => {
    if (!canCopyMorningSummary(summary, effectiveScope)) {
      setSummaryClock(Date.now());
      return;
    }
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary.whatsapp_text);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2500);
    } catch {
      setCopyState('failed');
      window.setTimeout(() => setCopyState('idle'), 2500);
    }
  };

  if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('insightsPrototype')) {
    return <InsightsWorkflowPrototype />;
  }

  return (
    <div className="space-y-4 px-3 py-3 pb-20">
      {showAdminReports && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Insights</div>
              <h2 className="text-base font-semibold text-slate-900">Admin Reports</h2>
            </div>
            <button
              type="button"
              onClick={() => setShowAdminReports(false)}
              className="rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700"
            >
              Close
            </button>
          </div>
          <AdminAnalyticsDashboard active={showAdminReports} isDesktop onOpenLead={onOpenLead} />
        </div>
      )}

      {canViewTeam ? (
        <div className="flex gap-1.5">
          {(['team', 'me'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium ${
                scope === s
                  ? 'bg-wechat-accent text-white'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
            >
              {s === 'team' ? 'Team' : 'Me'}
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-center text-xs font-medium text-slate-600">
          Me
        </div>
      )}

      <div className="flex items-center justify-end">
        <div className="flex gap-2">
          {canViewAdminReports && (
            <button
              type="button"
              onClick={() => setShowAdminReports(true)}
              className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
            >
              Admin Reports
            </button>
          )}
          <button
            type="button"
            onClick={() => void loadInsights(true)}
            disabled={refreshing}
            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {canViewMorningBrief && <AdminDashboardSnapshots active={active} />}

      {loading && <p className="py-8 text-center text-sm text-slate-500">Loading insights…</p>}
      {err && <p className="rounded-lg bg-red-50 p-3 text-center text-sm text-red-700">{err}</p>}

      {!loading && !err && data && (
        <>
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Act today</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">High-intent conversations first</h2>
                <p className="mt-1 text-xs text-slate-500">Calls stay unqualified until a person confirms relevant intent.</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${pendingDispositionCount ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{pendingDispositionCount} outcomes pending</span>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3"><div className="text-xs font-semibold text-emerald-800">WhatsApp</div><div className="mt-2 text-2xl font-semibold text-slate-900">{sourceFunnels.whatsapp.total}</div><div className="mt-1 text-xs text-slate-600">{sourceFunnels.whatsapp.qualified} qualified · {sourceFunnels.whatsapp.positive} positive</div></div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3"><div className="text-xs font-semibold text-blue-800">Facebook + Instagram</div><div className="mt-2 text-2xl font-semibold text-slate-900">{sourceFunnels.social.total}</div><div className="mt-1 text-xs text-slate-600">{sourceFunnels.social.qualified} qualified · {sourceFunnels.social.positive} positive</div></div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3"><div className="text-xs font-semibold text-amber-800">Google Business Profile</div><div className="mt-2 text-2xl font-semibold text-slate-900">{gbpStatus?.connection.ready ? gbpStatus.reviews.needs_reply : '—'}</div><div className="mt-1 text-xs text-slate-600">{gbpStatus?.connection.ready ? 'reviews need reply' : 'Setup required · no false zeroes'}</div></div>
              <div className="rounded-xl border border-slate-300 bg-slate-50 p-3"><div className="text-xs font-semibold text-slate-700">Unqualified call signals</div><div className="mt-2 text-2xl font-semibold text-slate-900">{sourceFunnels.call_signals.total_signals}</div><div className="mt-1 text-xs text-slate-600">{sourceFunnels.call_signals.qualified} intent-qualified · {sourceFunnels.call_signals.positive} positive</div></div>
            </div>
          </section>

          {canViewMorningBrief && (
            <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                    Morning WhatsApp Brief
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">
                    Evidence-led actions for today
                  </h3>
                  <p className="mt-1 text-xs text-slate-600">
                    Copy this into the digital marketing / CRM WhatsApp group after review.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={copyMorningSummary}
                  disabled={!summaryCopyable || refreshing}
                  className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm disabled:opacity-50"
                >
                  {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy'}
                </button>
              </div>

              {summaryErr && (
                <p className="mb-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">{summaryErr}</p>
              )}

              {summary && (
                <p className="mb-3 text-xs text-slate-600">
                  Snapshot: {new Date(summary.generated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST.
                  {!summaryCopyable && ' Refresh to get a current brief before copying.'}
                </p>
              )}

              {summary?.whatsapp_text && summary.scope === effectiveScope ? (
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-xl border border-emerald-100 bg-white p-3 text-xs leading-5 text-slate-700">
                  {summary.whatsapp_text}
                </pre>
              ) : (
                <p className="rounded-xl border border-emerald-100 bg-white p-3 text-xs text-slate-500">
                  Morning summary will appear here after CRMAI loads the latest lead and intelligence signals.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Card
              label="Total leads"
              value={totals.total_leads}
              sub={effectiveScope === 'me' ? 'assigned to me' : undefined}
              info={{
                title: 'What this means',
                description:
                  'This is the total number of leads currently visible in the selected scope. In Team view it counts the whole CRM team set. In Me view it counts only leads assigned to you.',
                example:
                  'If the team has 1,905 leads and 54 are assigned to you, Team shows 1,905 while Me shows 54.',
              }}
            />
            <Card
              label="New lead today"
              value={today.new_leads}
              sub={`${today.new_lead_calls ?? 0} unique calls to these leads`}
              info={{
                title: 'What this means',
                description:
                  'This counts leads that were created today in the selected scope. The small text shows how many unique calls were made to those same leads today.',
                example:
                  'If one lead was created yesterday and another lead was created after midnight, only the new lead today card counts the one created today. The subtext counts calls to that lead only.',
              }}
            />
            <Card
              label="Call attempts"
              value={today.total_attempts ?? today.total_calls ?? today.calls_made}
              sub={`${today.connected_calls ?? 0} connected · ${today.calls_made} leads dialed`}
              info={{
                title: 'What this means',
                description:
                  'Total call attempts made today in the selected scope across all leads. The subtext shows how many attempts resulted in a connected call and how many unique leads were dialed.',
                example:
                  'If 10 call attempts were made to 5 unique leads and 3 connected with patients, Call attempts shows 10, with 3 connected and 5 leads dialed.',
              }}
            />
            <Card
              label="Won : Lost (status)"
              value={`${conversion_rate.toFixed(2)}%`}
              sub={`${by_status.won ?? 0} marked Won / ${by_status.lost ?? 0} marked Lost`}
              info={{
                title: 'What this means',
                description:
                  'Leads with status Won divided by leads with status Lost, as a percentage, across all leads in the selected scope. It uses the lead status only; a lead counts as won in reports only when a registration with a reference is recorded.',
                example:
                  'If 3 leads are marked Won and 150 are marked Lost, this shows 2%. It is not the share of all leads that registered.',
              }}
            />
            <Card
              label="Marked Won today"
              value={today.wins_today}
              sub={`${today.registrations_today ?? '—'} registrations today`}
              info={{
                title: 'What this means',
                description:
                  'The big number counts leads whose status was changed to Won today. The small text counts registrations recorded for today with a registration reference, which is what reports treat as won.',
                example:
                  'If two leads are marked Won today but only one has a registration recorded, this shows 2 with 1 registration today.',
              }}
            />
            <Card
              label="Unassigned"
              value={totals.unassigned}
              sub="needs pickup"
              info={{
                title: 'What this means',
                description:
                  'These are leads that do not have a telecaller assigned yet and are still in the active lead pool. They need pickup before the team starts working them.',
                example:
                  'If 67 leads have no assigned telecaller, Unassigned shows 67 and those leads are waiting to be picked up.',
              }}
            />
          </div>

          <Card
            label="Avg call length"
            value={`${avgCall}s`}
            sub={`${totals.total_calls} calls, ${Math.round(totals.total_seconds / 60)}m total`}
          />

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Dispositions
                </p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900">
                  Call outcomes for today
                </h3>
                <p className="mt-1 text-xs text-slate-600">
                  Unique leads called today, patient outcomes, and today&apos;s wins. Each lead is counted once — connected takes priority over not connected.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDispositions((value) => !value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ${
                  showDispositions
                    ? 'bg-slate-900 text-white ring-slate-900'
                    : 'bg-white text-slate-700 ring-slate-200'
                }`}
              >
                {showDispositions ? 'Hide dispositions' : 'Show dispositions'}
              </button>
            </div>

            {showDispositions && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <Card label="Total Disposed / Contacted" value={dispositionSummary.total_disposed_count} />
                <Card label="Disposed Connected Count" value={dispositionSummary.disposed_connected_count} />
                <Card label="Disposed Not Connected Count" value={dispositionSummary.disposed_not_connected_count} />
                <Card
                  label="WhatsApp Contact"
                  value={
                    !canViewTeam
                      ? agentPerformance[0]?.unique_whatsapp_patients_replied_today ?? 0
                      : dispositionSummary.whatsapp_contact_count ?? 0
                  }
                />
                <Card
                  label="Registered today"
                  value={dispositionSummary.registered_today ?? '—'}
                  sub={`${dispositionSummary.converted_today} marked Won`}
                />
              </div>
            )}
          </div>

          {!canViewTeam && agentPerformance.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    My Telecalling
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">
                    Your live calling snapshot
                  </h3>
                  <p className="mt-1 text-xs text-slate-600">
                    This shows only your own telecalling activity inside Insights.
                  </p>
                </div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ring-1 ${presencePillClass(
                    agentPerformance[0].current_status,
                    agentPerformance[0].status_is_stale
                  )}`}
                >
                  {formatPresenceLabel(
                    agentPerformance[0].current_status,
                    agentPerformance[0].status_is_stale
                  )}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Card label="Calls today" value={agentPerformance[0].calls_today} />
                <Card label="7d calls" value={agentPerformance[0].calls_7d} />
                <Card
                  label="Missed"
                  value={agentPerformance[0].missed_calls_7d}
                  sub="last 7 days"
                />
                <Card
                  label="Follow-ups"
                  value={agentPerformance[0].due_followups}
                  sub={`${agentPerformance[0].overdue_followups} overdue`}
                />
              </div>
            </div>
          )}

          {effectiveScope === 'team' && (
            <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm">
              <div className="mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">WhatsApp Insights</p>
                <p className="mt-1 text-xs text-slate-500">Current global team activity.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Card label="Unique Replied Today" value={whatsappTeamInsights?.unique_replied_today ?? 0} />
                <Card label="Unread WhatsApp" value={whatsappTeamInsights?.unread_whatsapp ?? 0} />
              </div>
            </div>
          )}

          {effectiveScope === 'team' && (
            <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                    Manager Control Tower
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">
                    Multi-telecaller readiness
                  </h3>
                  <p className="mt-1 text-xs text-slate-600">
                    Use this to retire NeoDove safely: caller activity, due follow-ups, missed calls, and outcomes in one place.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAttendance((value) => !value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ${
                    showAttendance
                      ? 'bg-slate-900 text-white ring-slate-900'
                      : 'bg-white text-slate-700 ring-amber-200'
                  }`}
                >
                  {showAttendance ? 'Hide attendance' : 'Show attendance'}
                </button>
              </div>

              <div className="mb-3 grid grid-cols-2 gap-2">
                <Card label="Due follow-ups" value={followupSummary.pending_due} sub={`${followupSummary.overdue} overdue`} />
                <Card label="Pending follow-ups" value={followupSummary.pending_total} sub={`${followupSummary.due_today} due today`} />
              </div>

              {showAttendance && (
                <div className="mb-3 rounded-xl border border-orange-100 bg-white p-3">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Attendance / Break snapshot
                      </h4>
                      <p className="mt-1 text-xs text-slate-500">
                        Optional manager view. A live status older than 15 minutes is marked stale.
                      </p>
                    </div>
                    <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                      {attendanceSummary.total} active users
                    </span>
                  </div>

                  <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                    <Card label="Available" value={attendanceSummary.available} />
                    <Card label="Busy / call" value={attendanceSummary.busy + attendanceSummary.on_call} />
                    <Card label="Break" value={attendanceSummary.break} />
                    <Card label="Offline" value={attendanceSummary.offline} />
                    <Card label="Stale" value={attendanceSummary.stale} />
                  </div>

                  {agentPerformance.length === 0 ? (
                    <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                      Attendance appears after active telecaller users are created.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-slate-100">
                      <table className="min-w-[620px] w-full text-left text-xs">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Telecaller</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Last signal</th>
                            <th className="px-3 py-2">Active call</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {agentPerformance.map((agent) => (
                            <tr key={`attendance:${agent.email}`} className="text-slate-700">
                              <td className="px-3 py-2">
                                <div className="font-medium text-slate-900">{agent.name}</div>
                                <div className="text-[11px] text-slate-500">{agent.email}</div>
                              </td>
                              <td className="px-3 py-2">
                                <span
                                  className={`inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ring-1 ${presencePillClass(
                                    agent.current_status,
                                    agent.status_is_stale
                                  )}`}
                                >
                                  {formatPresenceLabel(agent.current_status, agent.status_is_stale)}
                                </span>
                              </td>
                              <td className="px-3 py-2">{formatLastSeen(agent.minutes_since_seen)}</td>
                              <td className="px-3 py-2">
                                {agent.current_call_uuid ? (
                                  <span className="font-mono text-[11px] text-slate-600">
                                    {agent.current_call_uuid}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">None</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {agentPerformance.length === 0 ? (
                <p className="rounded-xl border border-amber-100 bg-white p-3 text-xs text-slate-500">
                  No active telecaller performance rows yet. Create active telecaller users and work calls from CRMAI to populate this report.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-amber-100 bg-white">
                  <table className="min-w-[780px] w-full text-left text-xs">
                    <thead className="bg-amber-50 text-[11px] uppercase tracking-wider text-amber-800">
                      <tr>
                        <th className="px-3 py-2">Telecaller</th>
                        <th className="px-3 py-2">Active</th>
                        <th className="px-3 py-2">Hot/Warm</th>
                        <th className="px-3 py-2">Calls Today</th>
                        <th className="px-3 py-2">7d Calls</th>
                        <th className="px-3 py-2">Missed</th>
                        <th className="px-3 py-2">Avg</th>
                        <th className="px-3 py-2">Won/Lost</th>
                        <th className="px-3 py-2">Follow-ups</th>
                        <th className="px-3 py-2">Unique WhatsApp Patients Replied Today</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {agentPerformance.map((agent) => (
                        <tr key={agent.email} className="text-slate-700">
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-900">{agent.name}</div>
                            <div className="text-[11px] text-slate-500">{agent.email}</div>
                          </td>
                          <td className="px-3 py-2 font-semibold">{agent.active_leads}</td>
                          <td className="px-3 py-2">{agent.hot_leads}/{agent.warm_leads}</td>
                          <td className="px-3 py-2 font-semibold">{agent.calls_today}</td>
                          <td className="px-3 py-2">
                            {agent.calls_7d}
                            <span className="ml-1 text-slate-400">({agent.connected_calls_7d} connected)</span>
                          </td>
                          <td className={`px-3 py-2 ${agent.missed_calls_7d > 0 ? 'font-semibold text-red-600' : ''}`}>
                            {agent.missed_calls_7d}
                          </td>
                          <td className="px-3 py-2">{agent.avg_duration_7d}s</td>
                          <td className="px-3 py-2">{agent.wins_7d}/{agent.lost_7d}</td>
                          <td className={`px-3 py-2 ${agent.overdue_followups > 0 ? 'font-semibold text-red-600' : ''}`}>
                            {agent.due_followups}
                            <span className="ml-1 text-slate-400">({agent.overdue_followups} overdue)</span>
                          </td>
                          <td className="px-3 py-2 font-semibold">{agent.unique_whatsapp_patients_replied_today ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {dispositionReasons.length > 0 && (
                <div className="mt-3 rounded-xl border border-amber-100 bg-white p-3">
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    7-day win/loss reasons
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {dispositionReasons.map((item) => (
                      <span
                        key={`${item.status}:${item.reason}`}
                        className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700"
                      >
                        {item.status}: {item.reason} ({item.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Status breakdown
            </h3>
            {STATUSES.map((s) => (
              <Bar
                key={s}
                label={STATUS_LABEL[s]}
                count={by_status[s] ?? 0}
                total={totalLeads}
                color={STATUS_COLOR[s].replace(/text-\S+/, '').replace(/bg-(\w+)-100/, 'bg-$1-400')}
              />
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Tier mix
            </h3>
            <Bar label="Tier 1 — Hot" count={by_tier['1'] ?? 0} total={totalLeads} color="bg-red-400" />
            <Bar label="Tier 2 — Warm" count={by_tier['2'] ?? 0} total={totalLeads} color="bg-amber-400" />
            <Bar label="Tier 3 — Cold" count={by_tier['3'] ?? 0} total={totalLeads} color="bg-slate-400" />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Source attribution
            </h3>
            {Object.keys(by_source).length === 0 ? (
              <p className="text-xs text-slate-500">No data yet.</p>
            ) : (
              Object.entries(by_source)
                .sort((a, b) => b[1] - a[1])
                .map(([src, n]) => (
                  <Bar key={src} label={src} count={n} total={totalSources} color="bg-blue-400" />
                ))
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Campaigns (which Vobiz line / ad tag converts)
            </h3>
            {Object.keys(by_campaign).length === 0 ? (
              <p className="text-xs text-slate-500">No campaign data yet.</p>
            ) : (
              Object.entries(by_campaign)
                .sort((a, b) => b[1] - a[1])
                .map(([c, n]) => (
                  <Bar key={c} label={c} count={n} total={totalCampaigns} color="bg-indigo-400" />
                ))
            )}
          </div>

          {effectiveScope === 'team' && top_telecallers.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Top telecallers (active leads)
              </h3>
              <ul className="space-y-1.5">
                {top_telecallers.map((t) => (
                  <li
                    key={t.assigned_to}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-slate-700">{t.assigned_to}</span>
                    <span className="font-semibold text-slate-900">{t.n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
